import { Component, computed, input, output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { CalendarEvent, CalendarViewRange } from '../../models/calendar.models';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  standalone: true,
  selector: 'app-full-calendar',
  imports: [FullCalendarModule, NgbTooltipModule, CommonModule],
  templateUrl: './full-calendar.component.html',
  styleUrl: './full-calendar.component.scss'
})
export class FullCalendarComponent {
  eventClick = output<any>();
  taskClick = output<any>();
  onMoreLinkClick = output<string>();
  viewStartDate = output<string>();
  viewEndDate = output<string>();
  viewCenterDate = output<string>();

  viewRange = output<CalendarViewRange>();
  events = input<CalendarEvent[]>([]);
  showAllInCell = input<boolean>(true);
  allowDefaultMorePopup = input<boolean>(false);

  options = input<CalendarOptions>({});

  eventContent = input<TemplateRef<unknown>>();

  dayHeaderContent = input<TemplateRef<unknown>>();

  readonly #defaultOptions: CalendarOptions = {
    plugins: [interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'timeGridDay,timeGridWeek,dayGridMonth,listWeek'
    },
    initialView: 'dayGridMonth',
    datesSet: info => {
      this.viewEndDate.emit(info.end.toISOString().replace(/T.*$/, ''));
      this.viewStartDate.emit(info.start.toISOString().replace(/T.*$/, ''));
      const centerTime = (info.start.getTime() + info.end.getTime()) / 2;
      const centerDate = new Date(centerTime);
      this.viewCenterDate.emit(centerDate.toISOString().replace(/T.*$/, ''));
      this.viewRange.emit({ start: info.start.toISOString(), end: info.end.toISOString() });
    },
    eventClick: info => {
      const data = {
        lotNo: info.event._def.title,
        scheduledDate: info.event._instance?.range.start.toISOString().split('T')[0],
        id: info.event._def.publicId
      };
      this.eventClick.emit(data);
      if (info.event._def.extendedProps['taskCalFlag']) {
        this.taskClick.emit({
          data,
          taskId: info.event._def.extendedProps['taskId']
        });
      }
    },
    moreLinkClick: (info: any) => {
      if (!this.showAllInCell() && !this.allowDefaultMorePopup()) {
        this.onMoreLinkClick.emit(info.date.toISOString().replace(/T.*$/, ''));
        return 'none';
      }
      return undefined;
    },
    eventMouseEnter: (info: any) => {
      const tooltip = document.createElement('div');
      tooltip.className = 'calendar-tooltip';
      tooltip.innerHTML =
        info.event.title +
        '<br>' +
        info.event.start.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      info.el.appendChild(tooltip);
    },
    eventMouseLeave: (info: any) => {
      const tooltip = info.el.querySelector('.calendar-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    },
    weekends: true,
    dayMaxEventRows: false,
    height: '100%'
  };

  calendarOptions = computed<CalendarOptions>(() => ({
    ...this.#defaultOptions,
    dayMaxEvents: !this.showAllInCell(),
    ...this.options(),
    events: this.events()
  }));
}
